import { ref, watch, type Ref } from "vue";

import Urls from "@/autogen/urls";
import { useXsrfHeaders } from "@/lib/api/queries/utils";
import { LinkStatus } from "@/lib/link-checking/types";
import { useStore } from "@/store/stores/link-checker";

type LinkValidationStatus = "errored" | "not_ok" | "ok" | "pending";

const CHECKER_DEBOUNCE_DELAY = 2_000;

export const useLinkValidation = (
  modelValue: Ref<string>,
  options?: { enabled?: Ref<boolean> }
) => {
  const headers = useXsrfHeaders();
  const persister = useStore();
  const inputTouched = ref(false);

  const status = ref<LinkValidationStatus>();

  let deferred: Deferred<void> | undefined;

  watch(
    [inputTouched, modelValue],
    async ([isInputTouched, urlString], [, prevUrlString], cleanup) => {
      if (options?.enabled?.value === false) {
        status.value = undefined;
        return;
      }

      // Inline-debounce: create a promise resolved by timeout or focusout.
      if (isInputTouched && urlString !== prevUrlString) {
        const d = (deferred = createDeferred());

        const timeout = setTimeout(d.resolve, CHECKER_DEBOUNCE_DELAY);
        cleanup(() => {
          deferred = undefined;
          clearTimeout(timeout);
        });

        status.value = undefined;
        await d.promise;
      }

      if (!urlString?.trim()) {
        status.value = undefined;
        return;
      }

      let url: URL;
      try {
        url = new URL(urlString);
      } catch {
        status.value = "not_ok";
        return;
      }

      const href = url.href;
      const protocol = url.protocol;

      // Check persisted store first.
      {
        const persistedStatus = persister.getEntry(href);

        if (persistedStatus === LinkStatus.OK) {
          status.value = "ok";
          return;
        }
        if (persistedStatus === LinkStatus.NOT_OK) {
          status.value = "not_ok";
          return;
        }
      }

      if (protocol !== "https:" && protocol !== "http:") {
        status.value = "not_ok";
        return;
      }

      // Don't fire checks until the input has been focused.
      if (!isInputTouched) {
        return;
      }

      const controller = new AbortController();
      const signal = controller.signal;

      cleanup(() => controller.abort());

      status.value = "pending";

      try {
        const response = await fetch(Urls["check-urls"](), {
          method: "POST",
          headers: {
            ...headers.value,
            "content-type": "application/json",
          },
          body: JSON.stringify({ urls: [href] }),
          signal,
        });

        if (!response.ok) {
          status.value = "errored";
          return;
        }

        const data = await response.json();

        if (!data.urls.includes(href)) {
          status.value = "ok";
          persister.setEntry(href, LinkStatus.OK);
        } else {
          status.value = "not_ok";
          persister.setEntry(href, LinkStatus.NOT_OK);
        }
      } catch {
        if (signal.aborted) {
          return;
        }

        status.value = "errored";
      }
    },
    { immediate: true }
  );

  const onFocusIn = () => {
    inputTouched.value = true;
  };

  const onFocusOut = () => {
    deferred?.resolve();
  };

  return { status, onFocusIn, onFocusOut };
};

interface Deferred<T> {
  promise: Promise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: any) => void;
}

const createDeferred = <T>(): Deferred<T> => {
  let resolve!: (value: T | PromiseLike<T>) => void;
  let reject!: (reason?: any) => void;

  const promise = new Promise<T>((_resolve, _reject) => {
    resolve = _resolve;
    reject = _reject;
  });

  return { promise, resolve, reject };
};
